import "../../css/ProfilePage/ProfilePage.css";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  AccountData,
  NotificationType,
  OnboardingFlowType,
  TwilioPhoneVerificationStatus,
} from "@markit/common.types";
import { Colors } from "../../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import {
  getAccountSlice,
  getIncompleteOnboardingFlows,
  updateAccountData,
} from "../../redux/slices/accountSlice";
import { showNotificationBanner } from "../../utils/notificationUtils";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import { usePartialAccountUpdates } from "../../hooks/usePartialAccountUpdates";
import EditProfileDetails, { EditProfileError } from "./EditProfileDetails";
import RectangleButton from "../Buttons/RectangleButton";
import { isValidUserName } from "../../utils/stringUtils";
import {
  censoredWordsDetected,
  checkValidSocialMediaLink,
  compareTwoStringsInsensitive,
  deepCopy,
  detectedCensored,
  hasSubscription,
  userPhoneVerificationStatus,
} from "@markit/common.utils";
import {
  getUserDataByUsername,
  getUsernameSnap,
} from "../../utils/FirebaseUtils";
import AlertContainer from "../Containers/AlertContainer";
import { isDesktop } from "react-device-detect";
import OnboardingTourStep from "../Onboarding/OnboardingTourStep";
import { PROFILE_PICTURE_ONBOARDING_STEPS } from "../../utils/onboardingFlowUtils";

type ProfilePageEditProfileProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
};

const ProfilePageEditProfile = memo(function ProfilePageEditProfileFn({
  isVisible,
  setIsVisible,
}: ProfilePageEditProfileProps) {
  const dispatch = useDispatch();
  const { accountData } = useSelector(getAccountSlice);
  const incompleteOnboardingFlows = useSelector(getIncompleteOnboardingFlows);
  const [showError, setShowError] = useState<EditProfileError>({
    type: "",
    message: "",
  });
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const { accountSettings, updateAccountSettings } = usePartialAccountUpdates();

  // prevent background scrolling when the modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isVisible]);

  const hasProfilePicTour = useMemo(
    () =>
      incompleteOnboardingFlows.some(
        (flow) => flow.id === OnboardingFlowType.PROFILE_PIC
      ),
    [incompleteOnboardingFlows]
  );

  // checks all conditions for a valid profile
  const isValidProfileDetails = (accountSettings: AccountData) => {
    if (accountSettings.fullName === "") {
      return "Full name cannot be empty.";
    } else if (detectedCensored(accountSettings.fullName)) {
      return `Full name contains banned words and must be removed (${censoredWordsDetected(
        accountSettings.fullName
      )})`;
    } else if (accountSettings.username === "") {
      return "Username cannot be empty";
    } else if (
      !checkValidSocialMediaLink("instagram", accountSettings.instagram)
    ) {
      return "Invalid instagram url";
    } else if (
      !checkValidSocialMediaLink("linkedin", accountSettings.linkedin)
    ) {
      return "Invalid linkedin url";
    } else if (!checkValidSocialMediaLink("twitter", accountSettings.twitter)) {
      return "Invalid X/twitter url";
    } else if (!checkValidSocialMediaLink("tiktok", accountSettings.tiktok)) {
      return "Invalid tiktok url";
    } else if (!checkValidSocialMediaLink("spotify", accountSettings.spotify)) {
      return "Invalid spotify url";
    } else if (!checkValidSocialMediaLink("youtube", accountSettings.youtube)) {
      return "Invalid youtube url";
    }
    return "";
  };

  const isAccountPendingApproval = useMemo(() => {
    const accountStatus = userPhoneVerificationStatus(accountSettings);
    return (
      hasSubscription(accountSettings) &&
      (accountStatus === TwilioPhoneVerificationStatus.PENDING_REVIEW ||
        accountStatus === TwilioPhoneVerificationStatus.IN_REVIEW ||
        accountStatus === TwilioPhoneVerificationStatus.REJECTED)
    );
  }, [accountSettings]);

  const saveEditProfile = useCallback(async () => {
    if (
      isAccountPendingApproval &&
      !compareTwoStringsInsensitive(
        accountData.fullName,
        accountSettings.fullName
      )
    ) {
      setShowError({
        type: "fullName",
        message: "You cannot change this until your account is approved.",
      });
      return;
    }
    if (
      isAccountPendingApproval &&
      !compareTwoStringsInsensitive(
        accountData.username,
        accountSettings.username
      )
    ) {
      setShowError({
        type: "username",
        message: "You cannot change this until your account is approved.",
      });
      return;
    }
    const usernameLowercase = accountSettings.username.toLowerCase();
    if (!isValidUserName(usernameLowercase)) {
      setShowError({
        type: "username",
        message:
          "Username can only consist of alphanumeric characters, 3-30 characters in length, no spaces.",
      });
      return;
    }
    if (usernameLowercase !== accountSettings.username) {
      const usernameSnap = await getUsernameSnap(usernameLowercase);
      if (usernameSnap.exists()) {
        const usernameData = usernameSnap.data();
        const existingUserData = await getUserDataByUsername(
          usernameData.username
        );
        if (existingUserData && existingUserData.uid !== accountSettings.uid) {
          setShowError({
            type: "username",
            message: "Username already exists. Try another.",
          });
          return;
        }
      }
    }
    const isValidProfile = isValidProfileDetails(accountSettings);
    if (isValidProfile !== "") {
      setAlertText({ heading: isValidProfile, subHeading: "" });
      return;
    }
    const finalAccountSettings: AccountData = deepCopy(accountSettings);
    finalAccountSettings.username = usernameLowercase;
    dispatch(updateAccountData(finalAccountSettings));
    showNotificationBanner(dispatch, "Saved", NotificationType.AFFIRMATIVE);
    setIsVisible(false);
  }, [
    accountData.fullName,
    accountData.username,
    accountSettings,
    dispatch,
    isAccountPendingApproval,
    setIsVisible,
  ]);

  return (
    <>
      <PopupModalContainer
        headerComp={<span>Edit Profile</span>}
        valueComp={
          <div className="ColumnNormal" style={{ paddingBottom: 14 }}>
            <EditProfileDetails
              accountSettings={accountSettings}
              updateAccountSettings={updateAccountSettings}
              showError={showError}
              setShowError={setShowError}
              loading={false}
            />
            <OnboardingTourStep
              key="profile-pic-tour-step-2"
              flowType={OnboardingFlowType.PROFILE_PIC}
              target=".onboarding-profile-pic-photo-target"
              content={
                <>
                  Click on your profile picture to upload a square photo (works
                  best).
                  <br />
                  <br />
                  Hit &quot;Save&quot; when you&apos;re done!
                </>
              }
              stepNumber={2}
              totalSteps={PROFILE_PICTURE_ONBOARDING_STEPS}
              placement="right"
            />
          </div>
        }
        fixedBottomContent={
          <div
            className="AlignedRow"
            style={{ gap: 10, paddingInline: 14, paddingTop: 10 }}
          >
            <RectangleButton
              buttonLabel={"Cancel"}
              onPress={() => setIsVisible(false)}
              altTextColor={Colors.BLACK}
              altColor={Colors.WHITE1}
              containerStyles={{
                paddingBlock: 14,
                paddingInline: 12,
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
            <RectangleButton
              buttonLabel={"Save"}
              onPress={saveEditProfile}
              altTextColor={Colors.WHITE}
              altColor={Colors.BLACK}
              containerStyles={{ paddingBlock: 14, paddingInline: 12 }}
            />
          </div>
        }
        noExit
        closeOnOutsidePress={
          alertText.heading === "" && isDesktop && !hasProfilePicTour
        }
        closeModal={() => setIsVisible(false)}
        modalContentWidth={PopupModalContainerWidth.SMALL}
      />
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </>
  );
});

export default ProfilePageEditProfile;
